import React, { useState } from "react";
import { NavLink } from "react-router-dom";

const cardStyle = {
  background: "#ffffff",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  padding: "20px",
  marginBottom: "20px",
};

const sectionTitleStyle = {
  fontWeight: 700,
  color: "#334155",
  fontSize: "15px",
  marginBottom: "14px",
};

const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  border: "1px solid #e2e8f0",
  borderRadius: "6px",
  backgroundColor: "#fff",
  color: "#334155",
  fontSize: "14px",
};

const SECTION_OPTIONS = [
  { id: "executive-summary", label: "Executive Summary", orderKey: "executiveSummary", defaultOrder: 1 },
  { id: "asset-analysis", label: "Asset Analysis", orderKey: "assetAnalysis", defaultOrder: 2 },
  { id: "holding-analysis", label: "Holding Analysis", orderKey: "holdingAnalysis", defaultOrder: 3 },
  { id: "investment-snapshot", label: "Investment Snapshot", orderKey: "investmentSnapshot", defaultOrder: 4 },
  { id: "portfolio-summary", label: "Portfolio Summary", orderKey: "portfolioSummary", defaultOrder: 5 },
  { id: "unrealized-capital-gain", label: "Unrealized Capital Gain", orderKey: "unrealizedCapitalGain", defaultOrder: 6 },
  { id: "transaction-statement", label: "Transaction Statement", orderKey: "transactionStatement", defaultOrder: 7 },
  { id: "folio-details", label: "Folio Details", orderKey: "folioDetails", defaultOrder: 8 },
];

const initialSections = SECTION_OPTIONS.reduce((acc, s) => {
  acc[s.orderKey] = { enabled: s.id !== "transaction-statement", order: s.defaultOrder };
  return acc;
}, {});

// transaction-statement starts unchecked
initialSections.transactionStatement = { enabled: false, order: 7 };

export default function ComprehensivePortfolio() {
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, "0");
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const yyyy = today.getFullYear();
  const defaultDate = `${dd}-${mm}-${yyyy}`;

  const [evaluationDate, setEvaluationDate] = useState(defaultDate);
  const [sections, setSections] = useState(initialSections);
  const [products, setProducts] = useState({
    mutualFund: true,
    sharesBond: true,
    fixedDeposit: true,
    otherAssets: true,
  });
  const [hideFolioNo, setHideFolioNo] = useState(false);
  const [benchmark, setBenchmark] = useState("scheme"); // "none" | "scheme" | "broad"
  const [numHoldings, setNumHoldings] = useState("10");
  const [activeAssetsOnly, setActiveAssetsOnly] = useState(false);
  const [ccWithRm, setCcWithRm] = useState(true);

  const toggleSection = (orderKey) => {
    setSections((prev) => ({
      ...prev,
      [orderKey]: { ...prev[orderKey], enabled: !prev[orderKey].enabled },
    }));
  };

  const setSectionOrder = (orderKey, value) => {
    setSections((prev) => ({
      ...prev,
      [orderKey]: { ...prev[orderKey], order: parseInt(value, 10) },
    }));
  };

  const toggleProduct = (key) => {
    setProducts((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveSettings = () => {
    // TODO: API to save settings
  };

  const handleDownloadReport = () => {
    // TODO: generate / download report
  };

  const handleSendEmail = () => {
    // TODO: send on email
  };

  const handleSendWhatsapp = () => {
    // TODO: send on WhatsApp
  };

  const orderOptions = [1, 2, 3, 4, 5, 6, 7, 8];

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          {/* Breadcrumb and Header */}
          <div className="mb-4" style={cardStyle}>
            <div className="content-page-header content-page-headersplit">
              <h5>Comprehensive Portfolio</h5>
            </div>
            <nav
              aria-label="breadcrumb"
              style={{ "--bs-breadcrumb-divider": "none", marginBottom: "15px" }}
            >
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <NavLink to="/dashboard">Dashboard</NavLink> /
                </li>
                <li className="breadcrumb-item">
                  <NavLink to="/admin/portfolio/comprehensive">Portfolio Management</NavLink> /
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Comprehensive Portfolio
                </li>
              </ol>
            </nav>
          </div>

          {/* Evaluation Date */}
          <div style={cardStyle}>
            <div style={sectionTitleStyle}>Evaluation Date</div>
            <div className="d-flex flex-wrap align-items-center gap-3">
              <div className="d-flex align-items-center" style={{ maxWidth: 200 }}>
                <span
                  className="d-flex align-items-center justify-content-center bg-light rounded-start"
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #e2e8f0",
                    borderRight: "none",
                    color: "#64748b",
                  }}
                >
                  <i className="fa fa-calendar-alt" />
                </span>
                <input
                  type="text"
                  placeholder="DD-MM-YYYY"
                  value={evaluationDate}
                  onChange={(e) => setEvaluationDate(e.target.value)}
                  style={{
                    ...inputStyle,
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    flex: 1,
                  }}
                />
              </div>
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
                style={{ background: "#3b82f6", border: "none" }}
              >
                <i className="fa fa-user" />
                Select User
                <i className="fa fa-chevron-down ms-1" style={{ fontSize: "12px" }} />
              </button>
              <button
                type="button"
                className="btn d-flex align-items-center gap-2"
                style={{
                  background: "#10b981",
                  color: "#fff",
                  border: "none",
                  fontWeight: 600,
                }}
                onClick={handleSaveSettings}
              >
                Save Settings
              </button>
            </div>
          </div>

          {/* Choose Sections */}
          <div style={cardStyle}>
            <div style={sectionTitleStyle}>Choose Sections</div>
            <div className="row g-3">
              {SECTION_OPTIONS.map(({ id, label, orderKey }) => (
                <div key={id} className="col-12 col-md-6 col-lg-4">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <input
                      type="checkbox"
                      id={id}
                      checked={sections[orderKey]?.enabled ?? false}
                      onChange={() => toggleSection(orderKey)}
                      style={{ accentColor: "#10b981", width: 18, height: 18 }}
                    />
                    <label htmlFor={id} style={{ marginBottom: 0, flex: 1, cursor: "pointer" }}>
                      {label}
                    </label>
                    <select
                      value={sections[orderKey]?.order ?? 1}
                      onChange={(e) => setSectionOrder(orderKey, e.target.value)}
                      style={{
                        ...inputStyle,
                        width: "70px",
                        padding: "6px 8px",
                      }}
                    >
                      {orderOptions.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Choose Product */}
          <div style={cardStyle}>
            <div style={sectionTitleStyle}>Choose Product</div>
            <div className="d-flex flex-wrap gap-4">
              {[
                { key: "mutualFund", label: "Mutual Fund" },
                { key: "sharesBond", label: "Shares & Bond" },
                { key: "fixedDeposit", label: "Fixed Deposit" },
                { key: "otherAssets", label: "Other Assets" },
              ].map(({ key, label }) => (
                <label
                  key={key}
                  className="d-flex align-items-center gap-2 mb-0"
                  style={{ cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    checked={products[key]}
                    onChange={() => toggleProduct(key)}
                    style={{ accentColor: "#10b981", width: 18, height: 18 }}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* Display Option & Data Option - side by side */}
          <div className="row">
            <div className="col-12 col-md-6">
              <div style={cardStyle}>
                <div style={sectionTitleStyle}>Display Option</div>
                <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={hideFolioNo}
                    onChange={() => setHideFolioNo(!hideFolioNo)}
                    style={{ accentColor: "#10b981", width: 18, height: 18 }}
                  />
                  Hide Folio No.
                </label>
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div style={cardStyle}>
                <div style={sectionTitleStyle}>Data Option</div>
                <div className="mb-3">
                  <div className="d-flex flex-wrap gap-3 mb-2">
                    {[
                      { value: "none", label: "No Benchmark" },
                      { value: "scheme", label: "Scheme Benchmark" },
                      { value: "broad", label: "Broad Benchmark" },
                    ].map(({ value, label }) => (
                      <label
                        key={value}
                        className="d-flex align-items-center gap-2 mb-0"
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          type="radio"
                          name="benchmark"
                          checked={benchmark === value}
                          onChange={() => setBenchmark(value)}
                          style={{ accentColor: "#10b981" }}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="d-flex flex-wrap align-items-center gap-3">
                  <div className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: "14px", color: "#475569" }}>No. of holdings</span>
                    <select
                      value={numHoldings}
                      onChange={(e) => setNumHoldings(e.target.value)}
                      style={{ ...inputStyle, width: "80px" }}
                    >
                      {[5, 10, 15, 20, 25, 50].map((n) => (
                        <option key={n} value={String(n)}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                  <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={activeAssetsOnly}
                      onChange={() => setActiveAssetsOnly(!activeAssetsOnly)}
                      style={{ accentColor: "#10b981", width: 18, height: 18 }}
                    />
                    Active Assets only
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={cardStyle}>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn d-flex align-items-center gap-2"
                  style={{
                    background: "#10b981",
                    color: "#fff",
                    border: "none",
                    fontWeight: 600,
                  }}
                  onClick={handleDownloadReport}
                >
                  Download Report
                </button>
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center gap-2"
                  style={{ background: "#3b82f6", border: "none" }}
                  onClick={handleSendEmail}
                >
                  Send On Email
                </button>
                <button
                  type="button"
                  className="btn d-flex align-items-center gap-2"
                  style={{
                    background: "#10b981",
                    color: "#fff",
                    border: "none",
                    fontWeight: 600,
                  }}
                  onClick={handleSendWhatsapp}
                >
                  <i className="fa fa-whatsapp" style={{ fontSize: "18px" }} />
                  Send on Whatsapp
                </button>
              </div>
              <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={ccWithRm}
                  onChange={() => setCcWithRm(!ccWithRm)}
                  style={{ accentColor: "#10b981", width: 18, height: 18 }}
                />
                CC with RM
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
